import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { ShieldOff } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

const AccessContext = createContext({ ready: false, blocked: new Set(), catalog: [], isAllowed: () => true, moduleForPath: () => null, reload: () => {} });

let catalogCache = null;

export const AccessProvider = ({ children }) => {
  const { user } = useAuth();
  const [state, setState] = useState({ ready: false, blocked: new Set(), catalog: [] });

  const reload = useCallback(async () => {
    try {
      const me = await api.get('/access/me');
      const catalog = catalogCache || (catalogCache = (await api.get('/access/catalog')).data || []);
      setState({ ready: true, blocked: new Set(me.data?.blocked || []), catalog });
    } catch (e) {
      setState((s) => ({ ...s, ready: true }));
    }
  }, []);

  const { pathname } = useLocation();
  useEffect(() => { if (user) reload(); }, [user, pathname, reload]);

  const isAllowed = useCallback((key) => !key || !state.blocked.has(key), [state.blocked]);
  const moduleForPath = useCallback(
    (path) => state.catalog.find((m) => path === m.path || path.startsWith(`${m.path}/`))?.key || null,
    [state.catalog]
  );

  return (
    <AccessContext.Provider value={{ ...state, isAllowed, moduleForPath, reload }}>
      {children}
    </AccessContext.Provider>
  );
};

export const useAccess = () => useContext(AccessContext);

export const ModuleGuard = ({ children }) => {
  const { pathname } = useLocation();
  const { ready, isAllowed, moduleForPath } = useAccess();
  const key = moduleForPath(pathname);
  if (!ready) {
    return <div className="flex items-center justify-center h-48" data-testid="module-guard-loading"><div className="spinner" /></div>;
  }
  if (key && !isAllowed(key)) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] text-center gap-3" data-testid="module-blocked">
        <ShieldOff className="w-12 h-12 text-[#B85C38]" strokeWidth={1.4} />
        <p className="font-display text-xl font-bold text-[#2B3A2C]">Module non autorisé</p>
        <p className="text-sm text-gray-500 max-w-sm font-body-modern">
          Ce module n&apos;est pas activé pour votre profil. Contactez votre administrateur si vous en avez besoin.
        </p>
      </div>
    );
  }
  return children;
};
